var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EmptyState, FlexBox, IconButton, Modal, ModalBody, ModalFoot, ModalHead, Spinner, Text, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useState } from 'react';
import { searchApi } from 'apis/search';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { searchActions } from 'app/views/layout/views/search/search.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { SavedSearchFilters } from '../filters/savedSearchFilters';
import styles from './savedSearchListModal.module.scss';
export const SavedSearchListModal = createAppModal({ type: 'search:savedSearch:list' }, ({ isOpen, hide }) => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const [selected, setSelected] = useState();
    const activeSavedSearch = useAppSelector((state) => state.search.savedSearch);
    const { savedSearchList, isLoading } = searchApi.useReadSavedSearchListQuery(undefined, {
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { savedSearchList: (_b = data === null || data === void 0 ? void 0 : data.data.saved_searches) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    const [deleteSavedSearch] = searchApi.useDeleteSavedSearchMutation();
    const handleApply = () => {
        if (!selected)
            return;
        analytics.savedSearchApplied();
        dispatch(searchActions.savedSearch.apply(selected));
        notify({
            title: 'Saved search applied',
            text: `"${selected.name}" has been applied.`,
            className: 'success',
            icon: 'i-done',
            time: 5000,
        }, NotificationType.Success);
        hide === null || hide === void 0 ? void 0 : hide();
    };
    const handleDelete = (savedSearch) => __awaiter(void 0, void 0, void 0, function* () {
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: `Delete "${savedSearch.name}"`,
                    description: 'Are you sure you want to delete this saved search?',
                    confirm: {
                        label: 'Delete',
                    },
                },
            },
        }));
        if (isConfirmed) {
            try {
                yield deleteSavedSearch({ id: savedSearch.id });
                notify({
                    title: 'Saved search deleted',
                    text: `"${savedSearch.name}" has been deleted.`,
                    icon: 'i-done',
                    time: 5000,
                }, NotificationType.Success);
                if ((selected === null || selected === void 0 ? void 0 : selected.id) === savedSearch.id) {
                    setSelected(undefined);
                }
                analytics.savedSearchDeleted();
                return Promise.resolve({ isDone: true });
            }
            catch (error) {
                setSelected(undefined);
                notify({
                    title: 'Error while deleting the saved search.',
                }, NotificationType.Error);
            }
        }
    });
    const renderContent = () => {
        const getCardClassName = (savedSearch) => classnames({
            [styles.listCard]: true,
            [styles.listCardSelected]: (selected === null || selected === void 0 ? void 0 : selected.id) === savedSearch.id,
            [styles.listCardDisabled]: (activeSavedSearch === null || activeSavedSearch === void 0 ? void 0 : activeSavedSearch.id) === savedSearch.id,
        });
        if (isLoading) {
            return (_jsx(FlexBox, Object.assign({ justifyContent: "center" }, { children: _jsx(Spinner, { size: 32 }) })));
        }
        if (!savedSearchList.length) {
            return (_jsx(EmptyState, { title: "You haven't saved any searches yet", description: "Saving a search to come back to later is as easy as searching using a keyword or some filters and giving it a name.", buttonProps: {
                    children: 'Go back',
                    onClick: hide,
                } }));
        }
        return (_jsx("div", Object.assign({ className: styles.list }, { children: savedSearchList.map((savedSearch) => {
                const isActive = (activeSavedSearch === null || activeSavedSearch === void 0 ? void 0 : activeSavedSearch.id) === savedSearch.id;
                return (_jsxs(FlexBox, Object.assign({ className: getCardClassName(savedSearch), onClick: () => setSelected(savedSearch) }, { children: [_jsxs("div", { children: [_jsx(Text.Heading, { children: savedSearch.name }), _jsx(SavedSearchFilters, { variant: "text", params: savedSearch.params })] }), !isActive && (_jsx(IconButton, { variant: "tertiary", icon: "trash", onClick: () => handleDelete(savedSearch) }))] }), savedSearch.id));
            }) })));
    };
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, Object.assign({ title: "Saved searches", onClose: hide }, { children: "Here are the searches that you\u2019ve saved. Select one to use it again." })), _jsx(ModalBody, { children: renderContent() }), savedSearchList.length > 0 && (_jsx(ModalFoot, { confirmName: "Apply selected search", cancelName: "Cancel", confirmOnClick: handleApply, cancelOnClick: hide, confirmProps: {
                    disabled: !selected,
                } }))] })));
});
